import * as THREE from 'three';
import type { PoseProvider } from './PoseProvider';
import type { GeoWatcher } from '../core/GeoWatcher';
import type { OrientationSource } from '../core/OrientationSource';
import { normalizeDeg } from '../core/GeoMath';

const DEG2RAD = Math.PI / 180;
const EYE_HEIGHT_M = 1.5;
/**
 * GPSがこれ以上動いたらアライメントを再推定する(計画書§7.3)。
 * GPSノイズは屋外でも±10〜30m出るため、閾値が小さいとノイズを延々追いかけて
 * コンテンツが泳ぐ・跳ぶ(特に固定座標アンカー時に顕著)。
 */
const REALIGN_DISTANCE_M = 30;
/** 乖離がこの時間[ms]継続して初めて再推定する(瞬間的なノイズを無視) */
const REALIGN_SUSTAIN_MS = 2500;

/**
 * パスA: WebXR immersive-ar(ARCore VIO)。
 * カメラはthree.jsのWebXRManagerが毎フレーム更新するため、
 * このプロバイダは contentGroup に「ENU座標系→XRローカル座標系」変換を適用する。
 *
 * セッション開始時のGPS位置・コンパス方位を1回サンプリングしてアライメントを確定し、
 * 以後はVIOに任せる。GPSが大きく動いたら緩やかに再推定する。
 */
export class XrPoseProvider implements PoseProvider {
  readonly kind = 'xr' as const;
  private yawOffsetDeg = 0;
  private declinationDeg: number;
  private session: XRSession | null = null;
  private aligned = false;
  private alignHeadingDeg = 0; // アライメント時の真方位
  private alignEnu = new THREE.Vector3(); // アライメント時のENU位置(three座標: x=east, z=-north)
  private alignXrPos = new THREE.Vector3(); // アライメント時のXRローカルカメラ位置
  private targetMatrix = new THREE.Matrix4();
  private currentMatrix = new THREE.Matrix4();
  private onSessionEnd: (() => void) | null = null;
  /** GPSとVIOの乖離が閾値を超え続けている開始時刻(ノイズ無視用) */
  private deviationSince: number | null = null;
  /** セッション開始時刻(コンパス待ちタイムアウト用) */
  private sessionStartedAt = 0;

  constructor(
    private renderer: THREE.WebGLRenderer,
    private geo: GeoWatcher,
    private orientation: OrientationSource,
    magneticDeclinationDeg: number,
    private domOverlayRoot?: HTMLElement,
  ) {
    this.declinationDeg = magneticDeclinationDeg;
  }

  async start(): Promise<void> {
    this.geo.start();
    this.orientation.start(); // アライメント用に方位を取得(セッション中は使わない)

    const sessionInit: XRSessionInit = {
      requiredFeatures: ['local'],
      optionalFeatures: ['depth-sensing', 'dom-overlay'],
      ...(this.domOverlayRoot ? { domOverlay: { root: this.domOverlayRoot } } : {}),
      depthSensing: {
        usagePreference: ['gpu-optimized', 'cpu-optimized'],
        dataFormatPreference: ['luminance-alpha', 'float32'],
      },
    } as XRSessionInit;

    if (!navigator.xr) throw new Error('WebXRが利用できません');
    const session = await navigator.xr.requestSession('immersive-ar', sessionInit);
    this.session = session;
    this.renderer.xr.setReferenceSpaceType('local');
    await this.renderer.xr.setSession(session);
    this.sessionStartedAt = performance.now();
    session.addEventListener('end', () => {
      this.session = null;
      this.onSessionEnd?.();
    });
  }

  setOnSessionEnd(fn: () => void): void {
    this.onSessionEnd = fn;
  }

  get xrSession(): XRSession | null {
    return this.session;
  }

  stop(): void {
    void this.session?.end();
    this.geo.stop();
    this.orientation.stop();
  }

  setYawOffsetDeg(deg: number): void {
    this.yawOffsetDeg = deg;
    this.aligned = false; // 次フレームで再アライメント(現在のカメラ位置基準)
  }

  get ready(): boolean {
    return this.aligned;
  }

  update(camera: THREE.PerspectiveCamera, contentGroup: THREE.Group, _frame?: XRFrame): void {
    const geo = this.geo.current;
    if (!geo) return;

    if (!this.aligned) {
      // 瞬間値でなく平滑化した方位を使う(アライメントは一度きりのため、
      // その瞬間のセンサー乱れがそのまま恒久的な方位ズレになるのを防ぐ)
      let magHeading = this.orientation.smoothedMagneticHeadingDeg;
      if (magHeading === null) {
        // コンパス初期値待ち。ただし一定時間で諦めて仮アライメントする
        // (コンパスが取れない端末で「永遠に何も表示されない」状態を避ける。
        //  方位ズレは手動キャリブレーションで補正できる)
        if (performance.now() - this.sessionStartedAt < 5000) return;
        console.warn('[XrPoseProvider] コンパスが取得できないため仮方位(北)でアライメントします');
        magHeading = 0;
      }
      this.alignHeadingDeg = normalizeDeg(magHeading + this.declinationDeg + this.yawOffsetDeg);
      this.alignEnu.set(geo.east, EYE_HEIGHT_M, -geo.north);
      this.alignXrPos.copy(camera.position);
      this.computeAlignment(camera);
      this.currentMatrix.copy(this.targetMatrix);
      this.applyToGroup(contentGroup, this.currentMatrix);
      this.aligned = true;
      return;
    }

    // GPSが大きく動いたらアライメントの平行移動成分を緩やかに再推定。
    // ただし「大きな乖離が一定時間継続した」場合のみ(GPSノイズの瞬間値では動かさない)
    const curEnu = new THREE.Vector3(geo.east, EYE_HEIGHT_M, -geo.north);
    const xrDelta = new THREE.Vector3().subVectors(camera.position, this.alignXrPos);
    // VIOの移動量をENUに回した「期待ENU位置」と実GPSの乖離
    const yawRad = -this.alignHeadingDeg * DEG2RAD;
    const expectedEnu = xrDelta.clone().applyAxisAngle(THREE.Object3D.DEFAULT_UP, -yawRad).add(this.alignEnu);
    if (expectedEnu.distanceTo(curEnu) > REALIGN_DISTANCE_M) {
      if (this.deviationSince === null) {
        this.deviationSince = performance.now();
      } else if (performance.now() - this.deviationSince > REALIGN_SUSTAIN_MS) {
        this.alignEnu.lerp(new THREE.Vector3().subVectors(curEnu, expectedEnu).add(this.alignEnu), 0.008);
        this.computeAlignment(camera);
      }
    } else {
      this.deviationSince = null;
    }

    // 急なスナップを避けるため補間しながら適用
    lerpMatrix(this.currentMatrix, this.targetMatrix, 0.05);
    this.applyToGroup(contentGroup, this.currentMatrix);
  }

  /**
   * contentGroup変換 = T(alignXrPos) ∘ R_y(h) ∘ T(-alignEnu)
   * ENU点pの表示位置: R_y(h)·(p - alignEnu) + alignXrPos
   * h=真方位: 北(-Z)を向いていれば恒等、東(90°)を向いていればENUの+Xが-Zへ回る。
   */
  private computeAlignment(_camera: THREE.PerspectiveCamera): void {
    const h = this.alignHeadingDeg * DEG2RAD;
    const rot = new THREE.Matrix4().makeRotationY(h);
    const toOrigin = new THREE.Matrix4().makeTranslation(-this.alignEnu.x, -this.alignEnu.y, -this.alignEnu.z);
    const toXr = new THREE.Matrix4().makeTranslation(this.alignXrPos.x, this.alignXrPos.y, this.alignXrPos.z);
    this.targetMatrix.copy(toXr).multiply(rot).multiply(toOrigin);
  }

  private applyToGroup(group: THREE.Group, m: THREE.Matrix4): void {
    m.decompose(group.position, group.quaternion, group.scale);
  }
}

const _posA = new THREE.Vector3();
const _posB = new THREE.Vector3();
const _quatA = new THREE.Quaternion();
const _quatB = new THREE.Quaternion();
const _sclA = new THREE.Vector3();
const _sclB = new THREE.Vector3();

function lerpMatrix(current: THREE.Matrix4, target: THREE.Matrix4, alpha: number): void {
  current.decompose(_posA, _quatA, _sclA);
  target.decompose(_posB, _quatB, _sclB);
  _posA.lerp(_posB, alpha);
  _quatA.slerp(_quatB, alpha);
  _sclA.lerp(_sclB, alpha);
  current.compose(_posA, _quatA, _sclA);
}
